import { Component, EventEmitter, Input, Output } from '@angular/core';
import { openClose } from '@core/animations/animations';
import { createId } from '@core/util/file';
import { BrokerageType, Matter, MatterFileType } from '@models/matter';
import { MatterDocumentType } from '@models/matter-file';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Store, dispatch } from '@ngxs/store';
import {
	FileUpload,
	FileUploadState,
} from '@state/file-upload/fileUpload.state';
import { MatterDispatch } from '@state/matter';
import { CreateStatusType, UploadFileStatus } from '@state/utils/models';
import { map } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { TestHooks } from './matter-documents-upload.hooks';
import { NOTE_TYPES } from 'src/app/ui/note/note.component';
import { BusinessFeatureFlagSelectors } from '@state/business';
import { UserProfileState } from '@state/user-profile';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { OverwriteFields } from '@state/user-profile/actions';
@UntilDestroy()
@Component({
	selector: 'app-upload-documents-modal-step',
	templateUrl: './matter-documents-upload.component.html',
	styleUrls: ['./matter-documents-upload.scss'],
	animations: [openClose()],
	standalone: false,
})
export class MatterDocumentsUploadComponent {
	testHooks = TestHooks;
	AnalyticsEvent = AnalyticsEvent;
	private OverwriteFields = dispatch(OverwriteFields);
	public fileStatusFilter$ = this.store.select(FileUploadState.getFileStatus);
	public isIntroduceAiCreateMatterModalHidden$ = this.store.select(
		UserProfileState.isIntroduceAiCreateMatterModalHidden,
	);
	@ViewSelectSnapshot(SelectedMatter.getSelected) matter: Matter;
	@ViewSelectSnapshot(SelectedMatter.getLastestCreatedMatter)
	lastestCreatedMatter: Partial<Matter>;
	@Input() set matterId(matterId: string) {
		this._matterId = matterId;
	}

	get isAIEnabled$() {
		return this.store.select(BusinessFeatureFlagSelectors.isAIEnabled);
	}

	AI_Text$ = this.isAIEnabled$.pipe(
		map((isAIEnabled) => {
			return isAIEnabled
				? 'Discover our updated dual-screen view with the new AI-powered auto-scan feature.'
				: 'Our dual-screen view will make it easier for you to pull out relevant information for your file. It will save you time later, but if you prefer, you can skip this step.';
		}),
	);

	@Input() matterType: MatterFileType;

	@Output() onFinish = new EventEmitter();

	public _matterId: string;
	public apsDocId: string = createId();
	public sellerCommissionDocId: string = createId();
	public buyerCommissionDocId: string = createId();

	public fileCreatingStatus: CreateStatusType;
	public uploadingAPSStatus: UploadFileStatus;
	public uploadingSellerCommissionStatus: UploadFileStatus;
	public uploadingBuyerCommissionStatus: UploadFileStatus;
	public readingAPSfileStatus;
	public error;

	public ICON = ICON;
	public NOTE_TYPES = NOTE_TYPES;
	public brokerageType = BrokerageType;
	public matterFileType = MatterFileType;

	public apsFile: File;
	public sellerCommissionStatementFile: File;
	public buyerCommissionStatementFile: File;

	public matterDocumentTypes = MatterDocumentType;
	public uploading = false;

	public addCommissionStatements = false;
	public addBuyerCommissionStatement = false;
	public isAiApsToggleChecked = true;
	public isAiCommissionToggleChecked = true;

	public primaryAction;

	constructor(
		private matterDispatch: MatterDispatch,
		private store: Store,
		private analytics: AnalyticsService,
	) {
		this.getFileStatus$(this.apsDocId)
			.pipe(untilDestroyed(this))
			.subscribe((upload: FileUpload) => {
				if (this.uploading && upload) {
					this.uploadingAPSStatus = upload?.status;
					this.fileUploadSuccessCheck();
				}
			});

		this.getFileStatus$(this.sellerCommissionDocId)
			.pipe(untilDestroyed(this))
			.subscribe((upload: FileUpload) => {
				if (this.uploading && upload) {
					this.uploadingSellerCommissionStatus = upload?.status;
					this.fileUploadSuccessCheck();
				}
			});

		this.getFileStatus$(this.buyerCommissionDocId)
			.pipe(untilDestroyed(this))
			.subscribe((upload: FileUpload) => {
				if (this.uploading && upload) {
					this.uploadingBuyerCommissionStatus = upload?.status;
					this.fileUploadSuccessCheck();
				}
			});
	}

	fileUploadSuccessCheck() {
		const apsSuccess =
			!this.apsFile ||
			(this.apsFile && this.uploadingAPSStatus === UploadFileStatus.SUCCESS);

		const sellerCommissionSuccess =
			!this.sellerCommissionStatementFile ||
			(this.sellerCommissionStatementFile &&
				this.uploadingSellerCommissionStatus === UploadFileStatus.SUCCESS);

		const buyerCommissionSuccess =
			!this.buyerCommissionStatementFile ||
			(this.buyerCommissionStatementFile &&
				this.uploadingBuyerCommissionStatus === UploadFileStatus.SUCCESS);

		if (apsSuccess && sellerCommissionSuccess && buyerCommissionSuccess) {
			this.onFinish.emit();

			return;
		}

		const apsError =
			this.apsFile && this.uploadingAPSStatus === UploadFileStatus.FAILED;
		const sellerCommissionError =
			this.sellerCommissionStatementFile &&
			this.uploadingSellerCommissionStatus === UploadFileStatus.FAILED;
		const buyerCommissionError =
			this.buyerCommissionStatementFile &&
			this.uploadingBuyerCommissionStatus === UploadFileStatus.FAILED;

		if (apsError || sellerCommissionError || buyerCommissionError) {
			this.error = true;

			if (this.apsFile) {
				this.apsFile = null;
			}

			if (this.sellerCommissionStatementFile) {
				this.sellerCommissionStatementFile = null;
			}

			if (this.buyerCommissionStatementFile) {
				this.buyerCommissionStatementFile = null;
			}
		}

		const apsFileUploading =
			this.apsFile && this.uploadingAPSStatus === UploadFileStatus.UPLOADING;
		const sellerCommissionUploading =
			this.sellerCommissionStatementFile &&
			this.uploadingSellerCommissionStatus === UploadFileStatus.UPLOADING;
		const buyerCommissionUploading =
			this.buyerCommissionStatementFile &&
			this.uploadingBuyerCommissionStatus === UploadFileStatus.UPLOADING;

		if (
			!apsFileUploading &&
			!sellerCommissionUploading &&
			!buyerCommissionUploading
		) {
			this.uploading = false;
		}
	}

	getFileStatus$(docId) {
		return this.fileStatusFilter$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(docId)),
		);
	}

	onAPSFileSelected(file: File) {
		this.apsFile = file;
	}

	onCommissionStatementFileSelected(
		file: File | null,
		brokerageType: BrokerageType,
	) {
		if (brokerageType === BrokerageType.SELLER_BROKERAGE) {
			this.sellerCommissionStatementFile = file;
		}

		if (brokerageType === BrokerageType.BUYER_BROKERAGE) {
			this.buyerCommissionStatementFile = file;
		}
	}

	removeCommissionStatementFile(brokerageType: BrokerageType) {
		if (brokerageType === BrokerageType.SELLER_BROKERAGE) {
			this.sellerCommissionStatementFile = null;
		}

		if (brokerageType === BrokerageType.BUYER_BROKERAGE) {
			this.buyerCommissionStatementFile = null;
		}
	}

	removeAPSFile() {
		this.apsFile = null;
	}

	uploadFiles() {
		this.error = null;

		if (this.addBuyerCommissionStatement || this.addCommissionStatements) {
			this.matterDispatch.OverwriteFieldsByMatterId(
				this._matterId,
				'showBuyerBrokerage',
				true,
			);
		}

		if (
			!(
				this.apsFile ||
				this.sellerCommissionStatementFile ||
				this.buyerCommissionStatementFile
			)
		) {
			this.onFinish.emit();

			return;
		}

		this.uploading = true;

		const systemTags = undefined;

		if (this.apsFile) {
			this.matterDispatch.UploadMatterFile(
				this.apsDocId,
				this._matterId,
				this.apsFile.name,
				this.apsFile,
				this.matterDocumentTypes.APS,
				systemTags,
				this.isAiApsToggleChecked,
			);

			this.analytics.logEvent(
				this.isAiApsToggleChecked
					? AnalyticsEvent.APS_MODAL_AI_ON
					: AnalyticsEvent.APS_MODAL_AI_OFF,
			);
		}

		if (this.sellerCommissionStatementFile) {
			this.matterDispatch.UploadMatterFile(
				this.sellerCommissionDocId,
				this._matterId,
				this.sellerCommissionStatementFile.name,
				this.sellerCommissionStatementFile,
				this.matterDocumentTypes.SELLER_COMMISSION_STATEMENT,
				systemTags,
				this.isAiCommissionToggleChecked,
			);
		}

		if (this.buyerCommissionStatementFile) {
			this.matterDispatch.UploadMatterFile(
				this.buyerCommissionDocId,
				this._matterId,
				this.buyerCommissionStatementFile.name,
				this.buyerCommissionStatementFile,
				this.matterDocumentTypes.BUYER_COMMISSION_STATEMENT,
				systemTags,
				this.isAiCommissionToggleChecked,
			);
		}

		if (
			this.sellerCommissionStatementFile ||
			this.buyerCommissionStatementFile
		) {
			this.analytics.logEvent(
				this.isAiCommissionToggleChecked
					? AnalyticsEvent.APS_MODAL_COMMISSION_ON
					: AnalyticsEvent.APS_MODAL_COMMISSION_OFF,
			);
		}
	}

	get disableSkip() {
		return (
			this.sellerCommissionStatementFile ||
			this.buyerCommissionStatementFile ||
			this.apsFile
		);
	}

	onAiApsToggleChanged($event: boolean) {
		this.isAiApsToggleChecked = $event;
	}

	onAiCommissionToggleChanged($event: boolean) {
		this.isAiCommissionToggleChecked = $event;
	}

	hideIntroduceAiCreateMatterModal() {
		this.OverwriteFields({
			field: 'settings.hideNotifications.hideIntroduceAiCreateMatterModal',
			value: true,
		});
	}
}
